import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Maximize2, Angle, ChevronDown, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import * as z from "zod";
import { DaysSchema, SlotSchema, type Days } from "../../../lib/schedule-presets/art-metadata";
import { applySlotBindingDefaults } from "../../../lib/schedule-presets/slot-binding-defaults";
import { createMetadataValue, metadataDefinitions, metadataReference, unwrapMetadataSchema, type MetadataValues } from "../../../lib/schedule-presets/metadata-schema";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Input } from "../../ui/input";
import { NativeSelect } from "../../ui/native-select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../ui/collapsible";
import { ApplyDayLayoutControl } from "./ApplyDayLayoutControl";
import { MetadataArrayEditor } from "./MetadataArrayEditor";
import { MetadataDictionaryEditor } from "./MetadataDictionaryEditor";
import { MetadataFormatPicker } from "./MetadataFormatPicker";
import { PlacementNumberField } from "./PlacementNumberField";
import { MetadataElementLayout } from "./MetadataElementLayout";
import { useMetadataForm } from "./MetadataFormContext";
import { MetadataActionButton, MetadataActions, type MetadataAction } from "./MetadataActions";

const settingRow = "relative grid h-[34px] min-w-0 grid-cols-[92px_minmax(0,1fr)] items-center pl-2.5 hover:bg-white focus-within:z-10 focus-within:bg-white focus-within:shadow-[inset_0_0_0_1.5px_#185fa5] [&_label]:truncate [&_label]:pr-1.5 [&_label]:text-xs [&_label]:font-normal [&_label]:text-neutral-600 [&_svg]:invisible hover:[&_svg]:visible focus-within:[&_svg]:visible [&_svg]:right-2.5 [&_svg]:size-3.5";
const settingInput = "h-[33px] rounded-none border-0 bg-transparent p-0 pr-2.5 text-left text-[13px] text-neutral-900 shadow-none focus-visible:ring-0";

type FieldProps = {
  schema: z.ZodType;
  value: unknown;
  label: string;
  contentLabel?: string;
  labels: Record<string, string>;
  actions?: MetadataAction[];
  extraActions?: ReactNode;
  disabled?: boolean;
  binding?: string;
  reveal?: "open" | "focus";
  stacked?: boolean;
  onChange: (value: unknown) => void;
};

function isFieldVisible(schema: z.ZodType, record: MetadataValues) {
  if (metadataReference(schema) === "formats" && record.type === "icon") return false;
  const bindings = unwrapMetadataSchema(schema).meta()?.visibleForBindings;
  return !Array.isArray(bindings) || bindings.includes(record.bind);
}

function FieldGroup({ label, actions, children, reveal, summary }: { label: string; actions?: ReactNode; children: ReactNode; reveal?: "open" | "focus"; summary?: string }) {
  const [open, setOpen] = useState(Boolean(reveal));
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!reveal) return;
    setOpen(true);
    if (reveal !== "focus") return;
    const frame = requestAnimationFrame(() => {
      ref.current?.scrollIntoView({ block: "nearest" });
      ref.current?.querySelector("input")?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [reveal]);
  return (
    <Collapsible ref={ref} open={open} onOpenChange={setOpen} className="min-w-0 overflow-hidden rounded-lg border border-neutral-200 bg-white">
      <div className="flex min-h-10 items-center gap-1 px-2">
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="group h-auto min-h-10 min-w-0 flex-1 justify-start whitespace-normal px-1 text-left font-semibold">
            <ChevronDown className="shrink-0 text-muted-foreground transition-transform group-data-[state=closed]:-rotate-90" />
            <span className="min-w-0">
              <span className="block">{label}</span>
              {!open && summary && <span className="mt-0.5 block truncate text-xs font-normal text-muted-foreground">{summary}</span>}
            </span>
          </Button>
        </CollapsibleTrigger>
        {actions}
      </div>
      <CollapsibleContent className="space-y-2 border-t border-neutral-200 p-2">{children}</CollapsibleContent>
    </Collapsible>
  );
}

function NumberField({ id, value, onChange, disabled, className }: {
  id: string;
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  className?: string;
}) {
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  return (
    <Input id={id} disabled={disabled} type="number" step="any" value={text} className={`font-mono ${className ?? ""}`}
      onChange={event => {
        setText(event.target.value);
        const number = event.target.valueAsNumber;
        if (Number.isFinite(number)) onChange(number);
      }} onBlur={() => setText(String(value))} />
  );
}

export function MetadataField({ schema, value, label, contentLabel, labels, actions = [], extraActions, disabled, binding, reveal, stacked, onChange }: FieldProps) {
  const { t } = useTranslation();
  const { values } = useMetadataForm();
  const id = useId();
  const inner = unwrapMetadataSchema(schema);
  const present = value !== undefined;
  const optional = schema instanceof z.ZodOptional;
  if (metadataReference(schema) === "formats") {
    return <MetadataFormatPicker value={value} binding={binding} optional={optional} disabled={disabled} onChange={onChange} />;
  }
  const omittedValue = inner.meta()?.omittedValue;
  if (optional && inner instanceof z.ZodNumber && typeof omittedValue === "number") {
    if (label === labels.scale) return <PlacementNumberField size id={id} value={typeof value === "number" ? value : omittedValue}
      label={<Maximize2 className="size-3.5" />} title={label} disabled={disabled} onChange={next => onChange(next === omittedValue ? undefined : next)} />;
    if (label === labels.rotation) return <PlacementNumberField rotation id={id} value={typeof value === "number" ? value : omittedValue}
      label={<Angle className="size-3.5" />} title={label} disabled={disabled} onChange={next => onChange(next === omittedValue ? undefined : next)} />;
    return (
      <div className={settingRow}>
        <label htmlFor={id} className="text-xs font-medium">{label}</label>
        <NumberField id={id} value={typeof value === "number" ? value : omittedValue}
          disabled={disabled} className={settingInput}
          onChange={next => onChange(next === omittedValue ? undefined : next)} />
      </div>
    );
  }
  const fieldActions = [...actions];
  if (present && optional) {
    fieldActions.push({ label: t("metadata.remove"), icon: Trash2, destructive: true,
      onClick: () => onChange(undefined) });
  }
  const collection = inner instanceof z.ZodArray && inner.meta()?.coordinates !== true;
  const needsFolder = inner instanceof z.ZodObject || collection || optional || fieldActions.length > 0 || Boolean(extraActions);
  const objectValue = typeof value === "object" && value !== null ? value as MetadataValues : null;
  const objectFields = inner instanceof z.ZodObject ? Object.entries(inner.shape) as [string, z.ZodType][] : [];
  const isElement = inner instanceof z.ZodObject && ("bind" in inner.shape || "format" in inner.shape);
  const coordinateFields = objectValue && !isElement
    ? objectFields.filter(([key, childSchema]) => unwrapMetadataSchema(childSchema).meta()?.coordinates === true || (key === "rotation" && "origin" in (objectValue ?? {})))
    : [];
  const coordinateFieldNames = new Set(coordinateFields.map(([key]) => key));
  const content = present ? (
    <MetadataFieldValue schema={inner} value={value} label={contentLabel ?? label} labels={labels} disabled={disabled} stacked={stacked} excludedFields={coordinateFieldNames} onChange={onChange} />
  ) : null;
  if (!needsFolder) return content;
  const folderLabel = collection && Array.isArray(value) ? `${label} (${value.length})` : label;
  const summary = isElement && objectValue
    ? [objectValue.style, objectValue.format].filter(item => typeof item === "string").join(" · ")
    : undefined;

  return (
    <FieldGroup label={folderLabel} reveal={reveal} summary={summary} actions={
      <>
        {!present && <MetadataActionButton showLabel disabled={disabled} action={{
          label: t("metadata.enableDefinition"), icon: Plus,
          onClick: () => onChange(createMetadataValue(schema, values)),
        }} />}
        <MetadataActions actions={fieldActions} disabled={disabled} />
      </>
    }>
      {coordinateFields.length > 0 && (
        <MetadataElementLayout fields={coordinateFields.map(([key, coordinateSchema]) => ({
          key,
          content: <MetadataField
            schema={coordinateSchema}
            value={objectValue?.[key]}
            label={labels[key] ?? key}
            labels={labels}
            disabled={disabled}
            onChange={next => {
              if (!objectValue) return;
              const updated = { ...objectValue, [key]: next };
              if (next === undefined) delete updated[key];
              onChange(updated);
            }}
          />,
        }))} />
      )}
      {extraActions}
      {content}
    </FieldGroup>
  );
}

function MetadataFieldValue({ schema, value, label, labels, disabled, stacked, excludedFields = new Set(), onChange }: Omit<FieldProps, "actions" | "extraActions"> & { excludedFields?: Set<string> }) {
  const { t } = useTranslation();
  const { values } = useMetadataForm();
  const id = useId();

  if (schema instanceof z.ZodObject) {
    const record = value as MetadataValues;
    const catchall = schema.def.catchall;
    if (catchall && !(catchall instanceof z.ZodNever)) {
      return <MetadataDictionaryEditor schema={schema} value={record} labels={labels} disabled={disabled} onChange={onChange} />;
    }
    const fields = Object.entries(schema.shape) as [string, z.ZodType][];
    const visibleFields = fields.filter(([key, childSchema]) => {
      if (excludedFields.has(key)) return false;
      return isFieldVisible(childSchema, record);
    });
    const isElement = "bind" in schema.shape || "format" in schema.shape;
    const renderedFields = visibleFields.map(([key, child]) => ({
      key,
      content: (
          <MetadataField key={key} schema={child} value={record[key]}
            label={key === "rotation" && "origin" in schema.shape ? t("metadata.field.layoutRotation") : labels[key] ?? key}
            labels={labels} disabled={disabled} stacked={isElement || (key === "rotation" && "origin" in schema.shape)} binding={typeof record.bind === "string" ? record.bind : undefined}
            extraActions={schema === DaysSchema ? (
              <ApplyDayLayoutControl days={value as Days} dayKey={key as keyof Days} labels={labels} onChange={onChange} />
            ) : undefined}
            onChange={next => {
              const changed = { ...record, [key]: next };
              const slotBindingChanged = schema === SlotSchema && key === "bind";
              const updated = slotBindingChanged ? applySlotBindingDefaults(changed, values) : changed;
              if (next === undefined) delete updated[key];
              const formatSchema = schema.shape.format;
              if ((key === "bind" || key === "type") && formatSchema && !isFieldVisible(formatSchema, updated)) {
                delete updated.format;
              }
              onChange(updated);
            }} />
      ),
    }));
    if (isElement) return <MetadataElementLayout fields={renderedFields} />;
    return <div className="divide-y divide-neutral-200 overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50 [&>[data-slot=collapsible]]:rounded-none [&>[data-slot=collapsible]]:border-0">{renderedFields.map(field => field.content)}</div>;
  }

  if (schema instanceof z.ZodArray) {
    const items = value as unknown[];
    if (schema.meta()?.coordinates === true) {
      return (
        <div className="flex min-w-0 flex-[2] overflow-hidden rounded-lg border border-black/12 in-data-[placement-group]:contents">
          {items.map((item, index) => (
            <PlacementNumberField key={index} id={`${id}-${index}`} value={item as number}
              label={index === 0 ? "X" : "Y"} disabled={disabled}
              onChange={next => onChange(items.map((current, itemIndex) => itemIndex === index ? next : current))} />
          ))}
        </div>
      );
    }
    return <MetadataArrayEditor schema={schema} items={items} labels={labels} disabled={disabled} onChange={onChange} />;
  }

  if (schema instanceof z.ZodBoolean) {
    return (
      <div className="flex h-[34px] items-center justify-between gap-3 px-2.5">
        <label htmlFor={id} className="text-xs font-medium">{label}</label>
        <Checkbox id={id} disabled={disabled} checked={value === true} onCheckedChange={checked => onChange(checked === true)} />
      </div>
    );
  }

  const reference = metadataReference(schema);
  const choices = reference ? Object.keys(metadataDefinitions(values, reference)) : [];
  const current = String(value);
  if (reference && !choices.includes(current)) choices.unshift(current);

  if (schema instanceof z.ZodNumber && label === labels.rotation) return <PlacementNumberField rotation
    id={id} value={value as number} label={<Angle className="size-3.5" />} title={label} disabled={disabled} onChange={onChange} />;

  return (
    <div className={settingRow}>
      <label htmlFor={id} className="text-xs font-medium">{label}</label>
      {reference || schema instanceof z.ZodEnum ? (
        <NativeSelect id={id} disabled={disabled} value={current} className={`${settingInput} cursor-pointer pr-[26px] md:text-[13px]`} onChange={event => onChange(event.target.value)}>
          {(schema instanceof z.ZodEnum ? schema.options : choices).map(option => (
            <option key={String(option)} value={String(option)}>{labels[String(option)] ?? String(option)}</option>
          ))}
        </NativeSelect>
      ) : schema instanceof z.ZodNumber ? (
        <NumberField id={id} value={value as number} disabled={disabled} className={settingInput} onChange={onChange} />
      ) : (
        <Input id={id} disabled={disabled || schema instanceof z.ZodLiteral} value={current} className={settingInput}
          onChange={event => onChange(event.target.value)} />
      )}
    </div>
  );
}
